import { useEmitUiEvent } from '@/analytics/snowplow/useEmitUIEvent';
import { FF_GDC_RELEASE_1 } from '@/constants/featureFlags';
import { getAuthData } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import type { DoctorConsultation, NurseConsultation } from '@/types';
import { Link, Typography } from '@montugroup/design-system';
import { Box } from '@mui/material';
import { useCallback } from 'react';
import { CONSULT_STATUS_MAP } from './PatientConsultationDetailsCell.constants';
import { PatientConsultationDetailsButton } from './PatientConsultationDetailsCell.styled';
import { buildPatientConsultationUrl } from './PatientConsultationDetailsCell.utils';

const PatientConsultationDetailsCell = ({ consultation }: { consultation: NurseConsultation | DoctorConsultation }) => {
  const { flags } = useFeatureFlags();
  const { isDoc } = getAuthData();
  const emitEvent = useEmitUiEvent();

  const isGDCEnabled = flags[FF_GDC_RELEASE_1];
  const activeConsultStatusText = consultation.waiting_room_status
    ? CONSULT_STATUS_MAP[consultation.waiting_room_status] ?? null
    : null;

  const patientConsultationDetailUrl = buildPatientConsultationUrl({
    consultation,
    isConsultActive: Boolean(activeConsultStatusText),
    isDoc,
    isGDCEnabled,
  });

  // TODO: Move to centralised calendar tracking hook when calendar folder structure is established
  const trackStartConsult = useCallback(() => {
    if (!activeConsultStatusText) {
      return;
    }

    emitEvent(
      {
        component_name: 'StartConsultButton',
        event_name: 'start_consult',
        id: 'start_consult_btn',
      },
      {
        consultation: {
          consultation_id: String(consultation.id),
        },
      },
    );
  }, [emitEvent, activeConsultStatusText, consultation]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
      <Link href={patientConsultationDetailUrl} target="_blank" onClick={trackStartConsult}>
        <PatientConsultationDetailsButton variant={activeConsultStatusText ? 'contained' : 'outlined'}>
          {activeConsultStatusText ? 'Open Consult' : 'View'}
        </PatientConsultationDetailsButton>
      </Link>

      {activeConsultStatusText && (
        <Typography variant="body2" fontWeight="bold" whiteSpace="nowrap">
          {activeConsultStatusText}
        </Typography>
      )}
    </Box>
  );
};

export { PatientConsultationDetailsCell };
